import React from 'react';
import {
  Button,
  Tooltip,
  OverlayDrawer,
  DrawerHeader,
  DrawerHeaderTitle,
  DrawerBody,
  Tab,
  TabList,
  Text,
  Skeleton,
  SkeletonItem,
  type SelectTabData,
  type SelectTabEvent,
} from '@fluentui/react-components';
import {
  DismissRegular,
  DiamondFilled,
  CircleMultipleConcentricRegular,
  ChevronUpRegular,
  ChevronDownRegular,
} from '@fluentui/react-icons';
import { useFiscalEndReviewPageStyles } from '../styles/FiscalEndReviewPage.styles';
import { getTierColor } from '../constants/tiers';
import { formatCurrency, formatNoteText } from '../utils/formatters';
import { usePartnerAliases } from '../contexts/PartnerAliasContext';
import { NotesEditor, type NotesEditorHandle } from './NotesEditor';
import type { PartnerReviewItem, TierChangeRecord, Note } from '../models/partnerReview';

const tierIconStyle = (tier: string) => ({ color: getTierColor(tier), fontSize: '16px' });

interface PartnerDrawerProps {
  selectedPartner: PartnerReviewItem | null;
  onClose: () => void;
  drawerTab: string;
  onDrawerTabSelect: (e: SelectTabEvent, data: SelectTabData) => void;
  selectedPartnerIndex: number;
  sortedPartnersLength: number;
  onNavigate: (direction: 'up' | 'down') => void;
  tierChanges: TierChangeRecord[] | null;
  isLoadingHistory: boolean;
  notesEditorRef: React.RefObject<NotesEditorHandle | null>;
  initialNoteBody: string;
  initialNoteSystemPrefix: string | null;
  isLoadingNotes: boolean;
  allNotes: Note[] | null;
  onNoteSaved: (mpnId: string, body: string) => void;
  onNotesHistoryLink: () => void;
}

export const PartnerDrawer: React.FC<PartnerDrawerProps> = ({
  selectedPartner,
  onClose,
  drawerTab,
  onDrawerTabSelect,
  selectedPartnerIndex,
  sortedPartnersLength,
  onNavigate,
  tierChanges,
  isLoadingHistory,
  notesEditorRef,
  initialNoteBody,
  initialNoteSystemPrefix,
  isLoadingNotes,
  allNotes,
  onNoteSaved,
  onNotesHistoryLink,
}) => {
  const styles = useFiscalEndReviewPageStyles();
  const { getDisplayName } = usePartnerAliases();

  return (
    <OverlayDrawer
      open={selectedPartner !== null}
      onOpenChange={(_e, { open }) => { if (!open) onClose(); }}
      position="end"
      size="medium"
    >
      <DrawerHeader>
        <DrawerHeaderTitle
          action={
            <span className={styles.drawerNavActions}>
              <Tooltip content="Previous location" relationship="label">
                <Button
                  appearance="subtle"
                  aria-label="Previous location"
                  icon={<ChevronUpRegular />}
                  onClick={() => onNavigate('up')}
                  disabled={selectedPartnerIndex <= 0}
                  size="small"
                />
              </Tooltip>
              <Tooltip content="Next location" relationship="label">
                <Button
                  appearance="subtle"
                  aria-label="Next location"
                  icon={<ChevronDownRegular />}
                  onClick={() => onNavigate('down')}
                  disabled={selectedPartnerIndex < 0 || selectedPartnerIndex >= sortedPartnersLength - 1}
                  size="small"
                />
              </Tooltip>
              <Button
                appearance="subtle"
                aria-label="Close"
                icon={<DismissRegular />}
                onClick={onClose}
              />
            </span>
          }
        >
          {selectedPartner?.partnerName ? getDisplayName(selectedPartner.partnerName) : 'Partner Details'}
        </DrawerHeaderTitle>
      </DrawerHeader>
      <DrawerBody>
        <TabList
          selectedValue={drawerTab}
          onTabSelect={onDrawerTabSelect}
          style={{ marginTop: '4px' }}
        >
          <Tab value="overview">Overview</Tab>
          <Tab value="notesHistory">Notes history</Tab>
        </TabList>
        <div className={styles.drawerTabContent}>
          {drawerTab === 'overview' && selectedPartner && (
            <div>
              <Text className={styles.drawerSectionTitle} block>Partner information</Text>
              <div className={styles.detailsGrid}>
                <div className={styles.detailColumn}>
                  <div className={styles.detailItem}>
                    <Text className={styles.detailLabel}>TPID</Text>
                    <Text className={styles.detailValue}>{selectedPartner.tpId ?? '---'}</Text>
                  </div>
                  <div className={styles.detailItem}>
                    <Text className={styles.detailLabel}>Contract location</Text>
                    <Text className={styles.detailValue}>{selectedPartner.contractLocation ?? '---'}</Text>
                  </div>
                  <div className={styles.detailItem}>
                    <Text className={styles.detailLabel}>Global MPN ID</Text>
                    <Text className={styles.detailValue}>{selectedPartner.globalMpnId ?? '---'}</Text>
                  </div>
                </div>
                <div className={styles.detailColumn}>
                  <div className={styles.detailItem}>
                    <Text className={styles.detailLabel}>Current tier</Text>
                    <Text className={styles.detailValue}>
                      {selectedPartner.currentTier ? (
                        <span className="status-cell">
                          <DiamondFilled style={tierIconStyle(selectedPartner.currentTier)} />
                          {selectedPartner.currentTier}
                        </span>
                      ) : '---'}
                    </Text>
                  </div>
                  <div className={styles.detailItem}>
                    <Text className={styles.detailLabel}>TTM revenue</Text>
                    <Text className={styles.detailValue}>{formatCurrency(selectedPartner.ttmRevenue)}</Text>
                  </div>
                  <div className={styles.detailItem}>
                    <Text className={styles.detailLabel}>Location MPN ID</Text>
                    <Text className={styles.detailValue}>{selectedPartner.locationMpnId ?? '---'}</Text>
                  </div>
                </div>
              </div>
              <hr className={styles.drawerDivider} />
              <Text className={styles.drawerSectionTitle} block>History &amp; timeline</Text>
              {isLoadingHistory ? (
                <Skeleton>
                  {Array.from({ length: 3 }, (_, i) => (
                    <div key={i} style={{ display: 'flex', gap: '12px', marginBottom: '20px' }}>
                      <SkeletonItem shape="circle" size={20} />
                      <div style={{ flex: 1 }}>
                        <SkeletonItem style={{ width: '120px', height: '13px', marginBottom: '6px' }} />
                        <SkeletonItem style={{ width: '80px', height: '13px', marginBottom: '6px' }} />
                        <SkeletonItem style={{ width: '200px', height: '13px' }} />
                      </div>
                    </div>
                  ))}
                </Skeleton>
              ) : !tierChanges || tierChanges.length === 0 ? (
                <Text className={styles.mutedText}>No tier change history found for this partner.</Text>
              ) : (
                <div className={styles.timeline}>
                  {tierChanges.map((tc, i) => (
                    <div key={tc.id} className={styles.timelineEntry}>
                      <div className={styles.timelineTrack}>
                        <CircleMultipleConcentricRegular className={styles.timelineIcon} />
                        {i < tierChanges.length - 1 && <div className={styles.timelineLine} />}
                      </div>
                      <div className={styles.timelineContent}>
                        <Text className={styles.timelineDate} block>
                          {new Date(tc.timestamp).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                        </Text>
                        <Text className={styles.timelineAction} block>
                          {tc.status === 'Pending'
                            ? 'Pending review'
                            : tc.status === 'Denied'
                              ? (tc.type === 'Demotion' ? 'Demotion denied' : 'Promotion denied')
                              : tc.type === 'Demotion' ? 'Demoted' : 'Promoted'}
                        </Text>
                        <Text className={styles.timelineDetail} block>
                          {tc.previousTier} &rarr; {tc.newTier} by {tc.changedBy}
                        </Text>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {selectedPartner?.locationMpnId != null && (
            <div style={{ display: drawerTab === 'overview' ? undefined : 'none' }}>
              <NotesEditor
                ref={notesEditorRef}
                mpnId={selectedPartner.locationMpnId}
                initialBody={initialNoteBody}
                initialSystemPrefix={initialNoteSystemPrefix}
                isLoading={isLoadingNotes}
                onNoteSaved={onNoteSaved}
                onNotesHistoryClick={onNotesHistoryLink}
              />
            </div>
          )}
          {drawerTab === 'notesHistory' && (
            isLoadingNotes ? (
              <Skeleton>
                {Array.from({ length: 4 }, (_, i) => (
                  <div key={i} style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
                    <SkeletonItem style={{ width: '100px', height: '14px' }} />
                    <SkeletonItem style={{ width: '120px', height: '14px' }} />
                    <SkeletonItem style={{ flex: 1, height: '14px' }} />
                  </div>
                ))}
              </Skeleton>
            ) : !allNotes || allNotes.length === 0 ? (
              <Text className={styles.mutedText}>No notes found for this partner.</Text>
            ) : (
              <div className={styles.notesTable}>
                <div className={styles.notesHeaderRow}>
                  <Text className={styles.notesHeaderCell} style={{ width: '120px', flexShrink: 0 }}>Created by</Text>
                  <Text className={styles.notesHeaderCell} style={{ width: '160px', flexShrink: 0 }}>Time</Text>
                  <Text className={styles.notesHeaderCell} style={{ flex: 1 }}>Note</Text>
                </div>
                {allNotes.map((note) => (
                  <div key={note.id} className={styles.notesRow}>
                    <Text className={styles.notesCell} style={{ width: '120px', flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{note.createdBy || '---'}</Text>
                    <Text className={styles.notesCell} style={{ width: '160px', flexShrink: 0 }}>
                      {new Date(note.creationDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      {' '}
                      {new Date(note.creationDate).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                    </Text>
                    <Text className={styles.notesCell} style={{ flex: 1 }}>
                      {formatNoteText(note.systemPrefix, note.body)}
                    </Text>
                  </div>
                ))}
              </div>
            )
          )}
        </div>
      </DrawerBody>
    </OverlayDrawer>
  );
};
